import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Header } from 'components';
import Table from 'components/tables/table';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import useApi from 'utils/use-api';
import useTableData from 'utils/use-table-data';
import { Group } from 'types/group';
import Modal from 'components/layouts/modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from 'components/core/text-input';
import { SelectInput, MultiCheckInput } from 'components/core';
import wrapClick from 'utils/wrap-click';
import { User } from 'types/user';
import dayjs from 'dayjs';
import { useUrlState, hasPermission } from 'utils';

const GroupFormModal: FC<{
  open: boolean;
  setOpen: (v: boolean) => void;
  users: User[];
  initialValues: any;
  onSubmit: (values: any) => Promise<void>;
  title: string;
  actionLabel: string;
}> = ({ open, setOpen, users, initialValues, onSubmit, title, actionLabel }) => {
  const userOptions = users.map(u => ({
    value: String(u.id),
    label: `${u.firstName} ${u.lastName}`.trim() || u.email,
  }));

  const form = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required(),
      description: Yup.string().optional(),
      leaderId: Yup.number().optional(),
      members: Yup.array().of(Yup.number()).optional(),
    }),
    onSubmit: async values => {
      await onSubmit({
        ...values,
        leaderId: values.leaderId ? Number(values.leaderId) : undefined,
        members: (values.members as (string | number)[])?.map(m => Number(m)) || [],
      });
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={title}
      description=""
      size="5xl"
      renderActions={() => (
        <button
          type="button"
          onClick={wrapClick(() => form.handleSubmit())}
          className="inline-flex ml-3 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
        >
          {actionLabel}
        </button>
      )}
    >
      <div className="space-y-4">
        <TextInput id="name" label="Group Name" required {...form} />
        <TextInput id="description" label="Description" {...form} />
        <SelectInput id="leaderId" label="Group Leader" options={userOptions} placeholder="Select leader" {...form} />
        <MultiCheckInput id="members" label="Members" options={userOptions} {...form} />
      </div>
    </Modal>
  );
};

const GroupsPage: FC = () => {
  const api = useApi();
  const [skip, setSkip] = useState(0);
  const [limit] = useUrlState<number>('pageSize');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Group | null>(null);

  const { data: usersData } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: async () => {
      const res = await api.get('/users', { params: { skip: 0, limit: 100 } });
      return (res.data?.rows || []) as User[];
    },
    enabled: hasPermission(['user:view']),
  });

  const {
    data: groupsResponse,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['groups', skip, limit],
    queryFn: async () => {
      const res = await api.get('/groups', { params: { skip, limit: limit || 10 } });
      const rows = res.data as Group[];
      return { rows, total: rows.length };
    },
  });

  const groupsData = useTableData({
    rows: groupsResponse?.rows || [],
    count: groupsResponse?.total || 0,
  });

  const createGroupMutation = useMutation({
    mutationFn: async (values: any) => {
      const res = await api.post('/groups', values);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ id, values }: { id: number; values: any }) => {
      const res = await api.put(`/groups/${id}`, values);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete(`/groups/${id}`);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const TableColumns = () => (
    <tr>
      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Description</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Leader</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Members</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Updated</th>
      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
        <span className="sr-only">Actions</span>
      </th>
    </tr>
  );

  const TableRow = (group: Group) => (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{group.name}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{group.description || '—'}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
        {group.leader ? `${group.leader.firstName} ${group.leader.lastName}` : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
        <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
          {group.members?.length || 0} members
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{group.updatedAt ? dayjs(group.updatedAt).format('MMM DD, YYYY') : '—'}</td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
        <button onClick={() => setEditing(group)} className="inline-flex items-center gap-x-1 text-primary-600 hover:text-primary-900">
          <PencilSquareIcon className="h-4 w-4" />
          Edit
        </button>
        <button onClick={() => deleteGroupMutation.mutate(group.id)} className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900">
          <TrashIcon className="h-4 w-4" />
          Delete
        </button>
      </td>
    </tr>
  );

  const baseFormValues = {
    name: '',
    description: '',
    leaderId: undefined,
    members: [] as (number | string)[],
  };

  const userList = useMemo(() => usersData || [], [usersData]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <Header
        renderActions={() => (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            New Group
          </button>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <Table
                title="Groups"
                data={groupsData || { rows: [], total: 0, page: 1, pageSize: limit || 10, totalPages: 0 }}
                loading={isLoading}
                isRefetching={isRefetching}
                skip={skip}
                limit={limit || 10}
                setSkip={setSkip}
                showTableHeader={true}
                hasSearch={false}
                refetch={refetch}
                renderColumns={TableColumns}
                renderItem={TableRow}
              />
            </div>
          </div>
        </div>
      </div>

      {showCreate && (
        <GroupFormModal
          open={showCreate}
          setOpen={setShowCreate}
          users={userList}
          initialValues={baseFormValues}
          onSubmit={values => createGroupMutation.mutateAsync(values)}
          title="Create Group"
          actionLabel="Create Group"
        />
      )}
      {editing && (
        <GroupFormModal
          open={!!editing}
          setOpen={v => {
            if (!v) setEditing(null);
          }}
          users={userList}
          initialValues={{
            name: editing.name,
            description: editing.description || '',
            leaderId: editing.leaderId ? String(editing.leaderId) : undefined,
            members: editing.members?.map(m => String(m.userId)) || [],
          }}
          onSubmit={values => updateGroupMutation.mutateAsync({ id: editing.id, values })}
          title="Edit Group"
          actionLabel="Save Changes"
        />
      )}
    </main>
  );
};

export default GroupsPage;
